/**
 * Projects Page - Unified Content System
 * Displays all project cards (formerly on Jobs page)
 * Uses standardized ContentCard component
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiEdit2 } from 'react-icons/fi';
import { FaTrash } from 'react-icons/fa';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';
import EditableModal from '../components/EditableModal';
import RichTextEditor from '../components/RichTextEditor';
import useEditableContent from '../hooks/useEditableContent';
import ContentCard from '../components/ContentCard';
import ContentGrid from '../components/ContentGrid';

const Projects = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [categories, setCategories] = useState([]);

  // Use unified hook for job/project items
  const {
    items: projects,
    loading,
    showModal,
    isCreating,
    formData: editForm,
    updateFormData: setEditForm,
    confirmOpen,
    itemToDelete: projectToDelete,
    startCreate,
    startEdit,
    cancelEdit,
    save: saveEdit,
    deleteItem: deleteProject,
    confirmDelete,
    cancelDelete
  } = useEditableContent('job', { page: 'jobs', section: 'listings' });

  useEffect(() => {
    const getCats = async () => {
      try {
        const res = await api.get('/job-categories');
        setCategories(res.data.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    getCats();
  }, []);

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  const filterOptions = [{ value: 'all', label: 'All' }, ...categories];

  const handleStartCreate = () => {
    startCreate({
      category: categories[0]?.value || 'web',
      title: '',
      shortDescription: '',
      description: '',
      technologies: []
    });
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingText>Loading projects...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <ProjectsContainer>
      <ConfirmModal
        isOpen={confirmOpen}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        onConfirm={() => deleteProject(projectToDelete)}
        onClose={cancelDelete}
      />
      <Container>
        <HeroSection
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>Projects</Title>
          {isAuthenticated && isAdmin && (
            <AddButton onClick={handleStartCreate}>Add Project</AddButton>
          )}
        </HeroSection>
        <FilterSection>
          {filterOptions.map(opt => (
            <FilterButton
              key={opt.value}
              active={activeFilter === opt.value}
              onClick={() => setActiveFilter(opt.value)}
            >
              {opt.label}
            </FilterButton>
          ))}
        </FilterSection>
        
        <ContentGrid>
          {filteredProjects.map((project, index) => (
            <CardWrapper key={project._id}>
              {isAuthenticated && isAdmin && (
                <CardActions>
                  <EditIconButton onClick={() => startEdit(project)}>
                    <FiEdit2 />
                  </EditIconButton>
                  <DeleteIconButton onClick={() => confirmDelete(project)}>
                    <FaTrash />
                  </DeleteIconButton>
                </CardActions>
              )}
              <ContentCard
                to={`/projects/${project._id}`}
                category={project.category}
                title={project.title}
                description={project.description}
                excerpt={project.shortDescription}
                metadata={{
                  tags: project.data?.tags || [],
                  technologies: project.data?.technologies || []
                }}
                index={index}
              />
            </CardWrapper>
          ))}
        </ContentGrid>
        
        <EditableModal
          isOpen={showModal}
          isCreating={isCreating}
          onClose={cancelEdit}
          onSave={saveEdit}
          title={isCreating ? 'Add Project' : 'Edit Project'}
        >
          <label>Category</label>
          <select
            value={editForm.category || ''}
            onChange={e => setEditForm({ category: e.target.value })}
          >
            {categories.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <label>Title</label>
          <input
            type="text"
            value={editForm.title || ''}
            onChange={e => setEditForm({ title: e.target.value })}
            placeholder="Project Title"
          />
          <label>Short Description</label>
          <RichTextEditor
            value={editForm.shortDescription || ''}
            onChange={(content) => setEditForm({ shortDescription: content })}
            placeholder="Brief description for the card"
            height="150px"
          />
          <label>Full Description</label>
          <RichTextEditor
            value={editForm.description || ''}
            onChange={(content) => setEditForm({ description: content })}
            placeholder="Detailed description for the detail page"
            height="250px"
          />
        </EditableModal>
      </Container>
    </ProjectsContainer>
  );
};

// Styled Components
const ProjectsContainer = styled.div`min-height:100vh; padding:5rem 0;`;
const Container = styled.div`max-width:1200px; margin:0 auto; padding:0 2rem;`;
const HeroSection = styled.div`text-align:center; margin-bottom:3rem; display:flex; flex-direction:column; align-items:center; gap:1rem;`;
const Title = styled.h1`font-size:3rem; margin-bottom:0; background:var(--accent-gradient); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; @media (max-width:768px){font-size:2rem;}`;
const LoadingContainer = styled.div`min-height:60vh; display:flex; align-items:center; justify-content:center;`;
const LoadingText = styled.p`font-size:1.25rem; color:var(--text-secondary);`;
const AddButton = styled.button`padding:0.75rem 1.5rem; background:var(--accent-gradient); color:white; border:none; border-radius:2rem; font-weight:600; cursor:pointer; transition:all 0.3s ease; &:hover{transform:translateY(-2px); box-shadow:var(--shadow-md);}`;
const FilterSection = styled.div`display:flex; justify-content:center; gap:1rem; margin-bottom:3rem; flex-wrap:wrap;`;
const FilterButton = styled.button`padding:0.75rem 1.5rem; background:${({active})=>active?'var(--accent-gradient)':'var(--bg-secondary)'}; color:${({active})=>active?'white':'var(--text-primary)'}; border:none; border-radius:2rem; font-weight:600; cursor:pointer; transition:all 0.3s ease; &:hover{transform:translateY(-2px); box-shadow:var(--shadow-md);}`;

const CardWrapper = styled.div`
  position: relative;
`;

const CardActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;

  ${CardWrapper}:hover & {
    opacity: 1;
  }
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid var(--accent-primary);
  color: var(--accent-primary);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: var(--accent-primary);
    color: white;
    transform: scale(1.05);
  }
`;

const DeleteIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #ef4444;
    color: white;
    transform: scale(1.05);
  }
`;

export default Projects;

